import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDsTranslate, useDsServices, useDsNotify } from '../context/DsProvider';
import { Button } from './ui/Button';
import { formatDateTime } from '../utils/dateFormat';
import type { AuditEvent } from '../types/signing';
import styles from './AuditTrailView.module.scss';

interface AuditTrailViewProps {
  envelopeId: string;
}

type AuditDotColor = 'positive' | 'danger' | 'brand' | 'neutral';

function getDotColor(action: string): AuditDotColor {
  const upper = action.toUpperCase();
  if (['CREATED', 'SENT', 'SIGNED', 'COMPLETED'].includes(upper)) return 'positive';
  if (['DECLINED', 'VOIDED'].includes(upper)) return 'danger';
  if (['VIEWED', 'DOWNLOADED'].includes(upper)) return 'brand';
  return 'neutral';
}

function getDotClass(color: AuditDotColor): string {
  switch (color) {
    case 'positive': return styles.dotPositive ?? '';
    case 'danger': return styles.dotDanger ?? '';
    case 'brand': return styles.dotBrand ?? '';
    case 'neutral': return styles.dotNeutral ?? '';
    default: return styles.dotNeutral ?? '';
  }
}

function formatActionLabel(action: string): string {
  return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AuditTrailView({ envelopeId }: AuditTrailViewProps) {
  const t = useDsTranslate();
  const notify = useDsNotify();
  const { audit: auditService } = useDsServices();

  const { data: events, isLoading } = useQuery({
    queryKey: ['ds-auditEvents', envelopeId],
    queryFn: () => auditService!.getAuditEvents(envelopeId),
    enabled: !!envelopeId && !!auditService,
  });

  // Backend renders a Certificate-of-Completion PDF at
  // `GET /v1/envelopes/{id}/audit/pdf` (PDFBox, 4-column table, auto-paginated,
  // sliced at the latest ENVELOPE_REVISED event). Streams raw bytes with a
  // `Content-Disposition: attachment` header — browser triggers the download.
  const handleDownloadPdf = useCallback(async () => {
    if (!auditService) return;
    try {
      const response = await auditService.downloadAuditPdf(envelopeId);
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-trail-${envelopeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      notify({
        variant: 'critical',
        title: t('K_AUDIT_DOWNLOAD_ERROR', 'Failed to download audit trail PDF.'),
      });
    }
  }, [envelopeId, auditService, notify, t]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('K_LOADING', 'Loading...')}</div>
      </div>
    );
  }

  const sortedEvents = [...(events ?? [])].sort(
    (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t('K_AUDIT_TRAIL_TITLE', 'Audit Trail')}</h3>
        {sortedEvents.length > 0 && (
          <Button variant="secondary" size="sm" onClick={handleDownloadPdf}>
            {t('K_BUTTON_DOWNLOAD_AUDIT_PDF', 'Download PDF')}
          </Button>
        )}
      </div>

      {sortedEvents.length === 0 ? (
        <div className={styles.empty}>{t('K_AUDIT_NO_EVENTS', 'No audit events found.')}</div>
      ) : (
        <div className={styles.timeline}>
          {sortedEvents.map((event: AuditEvent) => {
            const color = getDotColor(event.action);
            return (
              <div key={event.id} className={styles.entry}>
                <span className={`${styles.dot} ${getDotClass(color)}`} />
                <div className={styles.content}>
                  <span className={styles.action}>{formatActionLabel(event.action)}</span>
                  {event.actorName && <span className={styles.actor}>{event.actorName}</span>}
                  {event.actorEmail && <span className={styles.actorEmail}>{event.actorEmail}</span>}
                  <span className={styles.time}>{formatDateTime(event.createdOn)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
